import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { AttachWorkOrderDialog } from '@/components/master/AttachWorkOrderDialog';
import { Button } from '@/components/ui/button';
import { fmtDate } from '@/lib/format';
import { AlertTriangle, LinkIcon, ExternalLink } from 'lucide-react';

/**
 * Iter39 — WO Review list.
 *
 * Route: /admin/wo-review (Owner-only). Lists Master Projects that have a
 * typed `work_order_number` but no `work_order_id` truly linked. These are
 * legacy records that need Owner action:
 *   - Attach an existing Work Order (opens the 2-step Attach dialog)
 *   - Leave as-is (no auto-link, no deletion)
 *
 * Nothing on this page mutates data silently. Only explicit Owner action
 * makes a change.
 */
export default function WOReviewPage() {
  const { user } = useAuth();
  const [orphans, setOrphans] = useState([]);
  const [busy, setBusy] = useState(false);
  const [attachTarget, setAttachTarget] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get('/master-projects/wo-review/orphans');
      setOrphans(r.data || []);
    } catch (e) { toast.error(parseErr(e)); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  if (user?.role !== 'owner') {
    return (
      <AppShell>
        <div className="p-6 max-w-[720px]">
          <div className="border rounded-md p-4 bg-[hsl(var(--surface-1))]"
               style={{ borderColor: 'hsl(var(--border))' }}
               data-testid="wo-review-forbidden">
            <div className="flex items-center gap-2 text-[hsl(var(--danger))] font-medium">
              <AlertTriangle size={14} /> Owner-only page
            </div>
            <div className="text-[13px] mt-1">
              This review list is restricted to the Studio Owner. Ask them if a project needs a WO attached.
            </div>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell breadcrumbs={[{ label: 'Admin', to: '/admin/settings' }, { label: 'WO Review' }]}>
      <div className="p-6 max-w-[1100px] mx-auto" data-testid="wo-review-page">
        <div className="flex items-center gap-3 mb-4">
          <h1 className="font-heading text-2xl font-semibold flex items-center gap-2">
            <LinkIcon size={20} /> Work Order review
          </h1>
          <span className="chip chip-warning">{orphans.length} to review</span>
        </div>

        <div className="border rounded-md p-3 mb-4 bg-[hsl(var(--surface-1))] text-[13px]"
             style={{ borderColor: 'hsl(var(--border))' }}
             data-testid="wo-review-notice">
          These Master Projects have a Work Order number typed in as text but are <b>not</b> actually linked
          to a Work Order record. No auto-link is done — only you can decide whether to attach an existing
          WO from the list or leave the record as-is.
        </div>

        {busy && <div className="text-[13px] text-[hsl(var(--foreground-muted))]">Loading…</div>}
        {!busy && orphans.length === 0 && (
          <div className="text-[13px] text-[hsl(var(--foreground-muted))] p-4 text-center border rounded-md"
               style={{ borderColor: 'hsl(var(--border))' }}
               data-testid="wo-review-empty">
            No orphan Work Order text on any active project. 🎉
          </div>
        )}
        {!busy && orphans.length > 0 && (
          <div className="border rounded-xl bg-[hsl(var(--card))] overflow-x-auto"
               style={{ borderColor: 'hsl(var(--border))' }}>
            <table className="tbl w-full" data-testid="wo-review-table">
              <thead>
                <tr>
                  <th>Project</th><th>Client</th><th>Typed WO #</th>
                  <th>Date</th><th>Created</th><th></th>
                </tr>
              </thead>
              <tbody>
                {orphans.map(o => (
                  <tr key={o.project_id} data-testid={`wo-review-row-${o.project_id}`}>
                    <td>
                      <div className="font-medium text-[13px]">{o.project_name}</div>
                      <div className="text-[11px] text-[hsl(var(--foreground-subtle))] font-mono">{o.project_code}</div>
                    </td>
                    <td className="text-[12.5px]">{o.client}</td>
                    <td className="font-mono text-[12px]">{o.work_order_number}</td>
                    <td className="text-[12px]">{o.work_order_date ? fmtDate(o.work_order_date) : '—'}</td>
                    <td className="text-[11.5px] text-[hsl(var(--foreground-muted))]">
                      {o.created_by}<br />{o.created_at ? fmtDate(o.created_at) : ''}
                    </td>
                    <td>
                      <div className="flex gap-1.5">
                        <Link to={`/projects/${o.project_id}`}
                              className="chip"
                              data-testid={`wo-review-view-${o.project_id}`}>
                          <ExternalLink size={11} className="mr-1" />Open
                        </Link>
                        <Button variant="outline" size="sm"
                                onClick={() => setAttachTarget({ id: o.project_id, project_name: o.project_name, client: o.client, code: o.project_code })}
                                data-testid={`wo-review-attach-${o.project_id}`}>
                          <LinkIcon size={11} className="mr-1" />Attach WO…
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {attachTarget && (
          <AttachWorkOrderDialog
            project={attachTarget}
            open={true}
            onClose={() => setAttachTarget(null)}
            onDone={() => { setAttachTarget(null); load(); }} />
        )}
      </div>
    </AppShell>
  );
}
